import { Controller } from "@hotwired/stimulus";

/**
 * News feed — a form of the author's screens that saves itself: two seconds
 * after the last change, the draft is sent and its state is shown
 * (« Saving… », « Draft saved », « Could not save »).
 *
 * A form of the page marked `data-news-carry` (« Publish », « Schedule »)
 * takes this form's fields with it: an option ticked a second before is
 * not lost to an autosave that had not left yet.
 */
export default class extends Controller {
  static targets = ["status", "kind", "event"];
  static values = { autosaveUrl: String, saving: String, saved: String, failed: String };

  connect() {
    this.carry = (event) => this.carryInto(event.target);
    document.addEventListener("submit", this.carry, true);
    // Confirmed, « Publish » takes the options: the pending autosave would only
    // mark the post as corrected once it is out.
    this.confirmed = (event) => {
      if (event.target !== this.element && event.target.matches?.("[data-news-carry]")) clearTimeout(this.timer);
    };
    document.addEventListener("turbo:submit-start", this.confirmed);
  }

  disconnect() {
    clearTimeout(this.timer);
    document.removeEventListener("submit", this.carry, true);
    document.removeEventListener("turbo:submit-start", this.confirmed);
  }

  carryInto(form) {
    if (form === this.element || !form.matches?.("[data-news-carry]")) return;
    // The pending autosave stays until the submission starts: the
    // confirmation of « Publish » may still be cancelled.
    form.querySelectorAll("[data-news-carried]").forEach((input) => input.remove());
    for (const [name, value] of new FormData(this.element)) {
      if (name === "_method" || name === "authenticity_token" || typeof value !== "string") continue;
      const input = document.createElement("input");
      Object.assign(input, { type: "hidden", name, value });
      input.dataset.newsCarried = "";
      form.appendChild(input);
    }
  }

  changed() {
    if (!this.autosaveUrlValue) return;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.save(), 2000);
  }

  kindChanged(event) {
    this.kindTargets.forEach((label) => label.classList.toggle("is-selected", label.contains(event.target)));
    if (this.hasEventTarget) this.eventTarget.hidden = event.target.value !== "event";
    this.changed();
  }

  async save() {
    this.show(this.savingValue);
    const body = new FormData(this.element);
    body.set("_method", "patch");
    try {
      const response = await fetch(this.autosaveUrlValue, {
        method: "POST",
        headers: { Accept: "application/json", "X-CSRF-Token": this.csrf },
        body,
        credentials: "same-origin",
      });
      this.show(response.ok ? this.savedValue : this.failedValue);
    } catch (_) {
      this.show(this.failedValue);
    }
  }

  show(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text;
  }

  get csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }
}
